import type { Permission } from '@cheqroom/api';
import { useMemo } from 'react';

import { type Props as PermissionsProps, usePermissions } from './use-permissions.hook';

export const createSpotcheckPermissions = (permissions: ReturnType<typeof usePermissions>) => {
	const { hasAnyPermission: _hasAnyPermission, useSpotchecks } = permissions;

	const hasAnyPermission = (...required: Permission[]) => useSpotchecks && _hasAnyPermission(...required);

	const canWriter = (own?: boolean) =>
		hasAnyPermission('SPOTCHECKS_WRITER') || (own && hasAnyPermission('SPOTCHECKS_WRITER_OWN'));

	return {
		canRead: (ownSpotcheck?: boolean) =>
			hasAnyPermission('SPOTCHECKS_READER') || (ownSpotcheck && hasAnyPermission('SPOTCHECKS_READER_OWN')),
		canCreate: () => canWriter(true),
		canUpdate: canWriter,
		canDelete: (ownSpotcheck?: boolean) =>
			hasAnyPermission('SPOTCHECKS_DELETER') || (ownSpotcheck && hasAnyPermission('SPOTCHECKS_DELETER_OWN')),
		canArchive: canWriter,
		canAddAttachment: (ownSpotcheck?: boolean) =>
			hasAnyPermission('SPOTCHECKS_ATTACHMENTS_OWN_WRITER') ||
			(ownSpotcheck && hasAnyPermission('SPOTCHECKS_OWN_ATTACHMENTS_OWN_WRITER')),
		canRemoveAttachment: (ownAttachment?: boolean, ownSpotcheck?: boolean) =>
			hasAnyPermission('SPOTCHECKS_ATTACHMENTS_DELETER') ||
			(ownAttachment && hasAnyPermission('SPOTCHECKS_ATTACHMENTS_OWN_DELETER')) ||
			(ownAttachment && ownSpotcheck && hasAnyPermission('SPOTCHECKS_OWN_ATTACHMENTS_OWN_DELETER')),
		canAddComment: () => hasAnyPermission('SPOTCHECKS_COMMENTS_OWN_WRITER'),
		canEditComment: (ownComment?: boolean, ownSpotcheck?: boolean) =>
			(ownComment && hasAnyPermission('SPOTCHECKS_COMMENTS_OWN_WRITER')) ||
			(ownComment && ownSpotcheck && hasAnyPermission('SPOTCHECKS_OWN_COMMENTS_OWN_WRITER')),
		canRemoveComment: (ownComment?: boolean, ownSpotcheck?: boolean) =>
			hasAnyPermission('SPOTCHECKS_COMMENTS_DELETER') ||
			(ownComment && hasAnyPermission('SPOTCHECKS_COMMENTS_OWN_DELETER')) ||
			(ownComment && ownSpotcheck && hasAnyPermission('SPOTCHECKS_OWN_COMMENTS_OWN_DELETER')),
	};
};

export const useSpotcheckPermissions = (props: PermissionsProps) => {
	const permissions = usePermissions(props);

	return useMemo(() => createSpotcheckPermissions(permissions), [permissions]);
};
